import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import {
    Armchair,
    ArrowRight,
    ArrowUpFromLine,
    CheckCircle2,
    Dumbbell,
    LifeBuoy,
    Menu,
    ShieldCheck,
    TrendingUp,
    X,
    Zap,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Logo, LogoMark } from "../components/Logo";
import { Reveal, SectionHead } from "../components/Reveal";
import Marquee from "../components/Marquee";
import TextSizeToggle from "../components/TextSizeToggle";
import { scrollToId } from "../lib/scroll";

const API = "/api";
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const LINKS = [
    ["Por qué importa", "es-why-it-matters"],
    ["Organizaciones", "es-organizations"],
    ["Aprenderá", "es-what-youll-learn"],
    ["Cómo funciona", "es-how-it-works"],
    ["Acerca de", "es-about"],
    ["Preguntas", "es-faq"],
];

function EsHeader() {
    const [open, setOpen] = useState(false);
    const go = (e, id) => {
        e.preventDefault();
        setOpen(false);
        // Scroll after the mobile menu unmounts, so the target position is final
        window.setTimeout(() => scrollToId(id), 80);
    };
    return (
        <header className="site-header-es sticky top-0 z-50 border-b border-clay bg-cream/90 backdrop-blur-md">
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8 md:h-20">
                <a href="#es-top" data-testid="es-header-logo" onClick={(e) => go(e, "es-top")} aria-label="Fall Safe Ready — volver al inicio">
                    <Logo />
                </a>
                <nav className="site-nav items-center" aria-label="Principal">
                    {LINKS.map(([label, id]) => (
                        <a
                            key={id}
                            href={`#${id}`}
                            data-testid={`es-header-link-${id}`}
                            onClick={(e) => go(e, id)}
                            className="whitespace-nowrap text-lg font-semibold text-ink-soft transition-colors duration-200 hover:text-teal-700"
                        >
                            {label}
                        </a>
                    ))}
                </nav>
                <div className="flex items-center gap-3">
                    <a
                        href="/"
                        data-testid="lang-switch-en"
                        className="lang-pill hidden h-12 items-center rounded-full border-2 border-ink px-4 text-base font-semibold text-ink transition-colors duration-200 hover:bg-ink hover:text-cream md:inline-flex"
                    >
                        EN
                    </a>
                    <TextSizeToggle />
                    <a href="#es-signup" data-testid="es-header-cta" onClick={(e) => go(e, "es-signup")} className="btn-primary hidden whitespace-nowrap md:inline-flex">
                        Únase a la lista
                    </a>
                    <button
                        type="button"
                        className="menu-toggle inline-flex h-12 w-12 items-center justify-center rounded-full border border-clay bg-white text-ink lg:hidden"
                        data-testid="es-header-menu-toggle"
                        aria-expanded={open}
                        aria-controls="es-mobile-nav"
                        aria-label={open ? "Cerrar menú" : "Abrir menú"}
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
                    </button>
                </div>
            </div>
            {open && (
                <div id="es-mobile-nav" className="border-t border-clay bg-cream px-5 pb-6 pt-2 lg:hidden">
                    <nav className="flex flex-col" aria-label="Menú móvil">
                        {LINKS.map(([label, id]) => (
                            <a
                                key={id}
                                href={`#${id}`}
                                data-testid={`es-header-mobile-link-${id}`}
                                onClick={(e) => go(e, id)}
                                className="border-b border-clay/60 py-3.5 text-lg font-semibold text-ink"
                            >
                                {label}
                            </a>
                        ))}
                    </nav>
                    <a
                        href="/"
                        data-testid="es-header-mobile-lang-en"
                        className="mt-5 inline-flex h-12 w-full items-center justify-center rounded-full border-2 border-ink text-base font-semibold text-ink"
                    >
                        View in English
                    </a>
                    <a href="#es-signup" data-testid="es-header-mobile-cta" onClick={(e) => go(e, "es-signup")} className="btn-primary mt-3 w-full">
                        Únase a la lista de interés
                    </a>
                </div>
            )}
        </header>
    );
}

function EsHero() {
    const line = (children, i) => (
        <span className="block overflow-hidden pb-1.5">
            <span className="block">{children}</span>
        </span>
    );
    return (
        <section id="es-top" className="relative overflow-hidden pb-16 pt-10 sm:pt-14 lg:pb-24 lg:pt-20">
            <div aria-hidden="true" className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full border-[3px] border-teal-600/15" />
            <div aria-hidden="true" className="pointer-events-none absolute right-24 top-40 h-24 w-24 rounded-full bg-brand-amber/20" />
            <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10">
                <div className="lg:col-span-7">
                    <p className="mb-6 flex items-center gap-3 text-base font-bold uppercase tracking-[0.18em] text-teal-800">
                        <span className="h-[3px] w-10 rounded-full bg-brand-amber" aria-hidden="true" />
                        Entrenamiento en grupos pequeños · Arlington, Virginia
                    </p>
                    <h1 className="font-display text-[2.55rem] font-black leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-[4.35rem]">
                        {line(
                            <>
                                Listo para caer con{" "}
                                <span className="relative inline-block text-teal-700">
                                    seguridad.
                                    <svg aria-hidden="true" viewBox="0 0 120 12" preserveAspectRatio="none" className="absolute -bottom-1 left-0 h-3 w-full">
                                        <path d="M3 9c30-6 80-6 114-3" stroke="#F59E0B" strokeWidth="5" fill="none" strokeLinecap="round" />
                                    </svg>
                                </span>
                            </>,
                            0
                        )}
                        {line("Levántese con confianza.", 1)}
                    </h1>
                    <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-soft">
                        Entrenamiento en grupos pequeños para adultos activos de 55+ en Arlington, Virginia.
                        Aprenda qué hacer antes, durante y después de una caída, a su propio ritmo.
                    </p>
                    <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
                        <a
                            href="#es-signup"
                            data-testid="es-hero-cta-interest"
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToId("es-signup");
                            }}
                            className="btn-primary text-lg"
                        >
                            Únase a la lista de interés
                        </a>
                        <a
                            href="#es-organizations"
                            data-testid="es-hero-cta-organizations"
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToId("es-organizations");
                            }}
                            className="btn-outline text-lg"
                        >
                            Traiga un taller a su organización
                        </a>
                    </div>
                    <p className="mt-6 flex items-center gap-2.5 text-base font-bold text-ink" data-testid="es-hero-timeline">
                        <span className="relative flex h-2.5 w-2.5">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-amber opacity-60" />
                            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-amber" />
                        </span>
                        Primeros talleres a principios de 2027
                    </p>
                </div>
                <div className="lg:col-span-5">
                    <div className="relative mx-auto max-w-md lg:max-w-none">
                        <div aria-hidden="true" className="absolute -left-5 -top-5 h-28 w-28 rounded-tl-[2rem] border-l-4 border-t-4 border-brand-amber" />
                        <img
                            src="/images/hero-mats.jpg"
                            alt="Three panels of an older woman practicing a squat, a seated hold and a floor exercise on a padded mat while her coach watches"
                            className="aspect-[16/10] w-full rounded-[2rem] border border-clay object-cover shadow-[0_24px_60px_-24px_rgba(11,25,44,0.35)]"
                            loading="eager"
                            fetchPriority="high"
                            data-testid="es-hero-image"
                        />
                        <div className="absolute -bottom-6 left-6 max-w-[260px] rounded-2xl border border-clay bg-cream px-5 py-4">
                            <p className="font-display text-base font-extrabold text-ink">Equilibrio, paso a paso</p>
                            <p className="text-base text-ink-soft">Práctica gradual sobre colchonetas</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function EsStats() {
    const STATS = [
        { pre: "Más de", value: "1 de cada 4", text: "adultos de 65 años o más se caen cada año.", source: "Fuente: CDC" },
        { pre: "Cerca de", value: "4.5 millones", text: "visitas a departamentos de emergencia cada año son por caídas de adultos mayores.", source: "Fuente: CDC" },
        { pre: "", value: "88%", text: "de las visitas a emergencias y hospitalizaciones por fractura de cadera son causadas por caídas.", source: "Fuente: CDC" },
    ];
    return (
        <section id="es-why-it-matters" className="scroll-mt-24 py-20 lg:py-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal>
                    <SectionHead eyebrow="Lo que está en juego" title="Por qué importa" />
                </Reveal>
                <div className="mt-12 grid gap-6 md:grid-cols-3">
                    {STATS.map((s, i) => (
                        <Reveal key={s.value} delay={i * 0.1} className="h-full">
                            <div className="h-full rounded-3xl border border-clay bg-white p-8" data-testid={`es-stat-card-${i + 1}`}>
                                <p className="min-h-[1.5em] text-base font-bold uppercase tracking-wide text-teal-800">{s.pre || "\u00A0"}</p>
                                <p className="mt-1 font-display text-5xl font-black tracking-tight text-ink lg:text-[3.2rem] lg:leading-[1.05]">
                                    {s.value}
                                </p>
                                <p className="mt-4 text-lg leading-relaxed text-ink-soft">{s.text}</p>
                                <span className="mt-6 block h-1 w-12 rounded-full bg-brand-amber" aria-hidden="true" />
                                <p className="mt-3 text-base font-semibold text-ink-soft">{s.source}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.15}>
                    <p className="mt-12 max-w-3xl text-lg leading-relaxed text-ink">
                        Las caídas son la principal causa de lesiones para las personas de 65 años o más en EE.
                        UU. (National Council on Aging). Incluso una caída sin lesiones puede dejar a alguien con
                        miedo de moverse, y moverse menos aumenta el riesgo de la próxima caída.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

function EsPrevention() {
    const COLUMNS = [
        { icon: ShieldCheck, index: "01", title: "Prevención", text: "Los programas de fuerza y equilibrio reducen la frecuencia de las caídas. Funcionan, y los recomendamos." },
        { icon: LifeBuoy, index: "02", title: "Preparación", text: "Las caídas ocurren de todos modos. Fall Safe Ready le enseña cómo reaccionar cuando pierde el equilibrio, cómo caer de manera que proteja su cabeza, caderas y muñecas, y cómo levantarse." },
    ];
    return (
        <section className="pb-20 lg:pb-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal>
                    <SectionHead eyebrow="Nuestro enfoque" title="La prevención reduce las probabilidades. La preparación reduce el daño." />
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-6 max-w-3xl text-lg leading-relaxed text-ink-soft">
                        Nos ponemos el cinturón de seguridad aunque no planeamos chocar. Fall Safe Ready funciona
                        igual: se suma a los programas de prevención de caídas que usted ya conoce.
                    </p>
                </Reveal>
                <div className="mt-12 grid gap-6 md:grid-cols-2">
                    {COLUMNS.map((c, i) => (
                        <Reveal key={c.title} delay={i * 0.1} className="h-full">
                            <div className={`relative h-full rounded-3xl border p-8 lg:p-10 ${i === 1 ? "border-teal-600/30 bg-teal-50" : "border-clay bg-white"}`}>
                                <span className="absolute right-8 top-8 font-display text-2xl font-black text-brand-amber" aria-hidden="true">
                                    {c.index}
                                </span>
                                <span className="grid h-14 w-14 place-items-center rounded-2xl bg-teal-700 text-white" aria-hidden="true">
                                    <c.icon className="h-7 w-7" />
                                </span>
                                <h3 className="mt-6 font-display text-2xl font-extrabold text-ink">{c.title}</h3>
                                <p className="mt-3 text-lg leading-relaxed text-ink-soft">{c.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function EsPrograms() {
    const PROGRAMS = [
        { name: "Otago Exercise Program", fit: "Para adultos mayores frágiles o de alto riesgo que necesitan trabajo individualizado de fuerza y equilibrio" },
        { name: "A Matter of Balance", fit: "Para adultos cuyo miedo a caerse les impide ser activos" },
        { name: "SAIL (Stay Active and Independent for Life)", fit: "Para ejercicio continuo de fuerza y equilibrio en grupo" },
        { name: "Fall Safe Ready", fit: "Para adultos activos listos para practicar cómo atajarse, caer con más seguridad y levantarse del piso", highlight: true },
    ];
    const POINTS = [
        { icon: ShieldCheck, title: "Primero la seguridad", text: "Cada participante completa un cuestionario de salud, y cualquier persona a quien le vaya mejor en Otago o A Matter of Balance es referida allí." },
        { icon: Armchair, title: "Fácil de organizar", text: "Un taller introductorio de una hora solo necesita un salón, sillas resistentes y algo de espacio abierto en el piso." },
        { icon: TrendingUp, title: "Medible", text: "Los participantes completan evaluaciones simples antes y después, para que pueda ver los resultados." },
    ];
    return (
        <section id="es-organizations" className="scroll-mt-24 pb-20 lg:pb-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal>
                    <SectionHead eyebrow="Para organizaciones y aliados" title="Un siguiente paso práctico para las personas que usted sirve." />
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-6 max-w-3xl text-lg leading-relaxed text-ink-soft">
                        Fall Safe Ready está diseñado para funcionar junto con los programas establecidos de
                        prevención de caídas. Aquí es donde encaja:
                    </p>
                </Reveal>
                <Reveal delay={0.15}>
                    <ul className="mt-10 overflow-hidden rounded-3xl border border-clay bg-white">
                        {PROGRAMS.map((p) => (
                            <li
                                key={p.name}
                                data-testid={p.highlight ? "es-programs-row-fall-safe-ready" : undefined}
                                className={`grid gap-2 border-b border-clay p-6 last:border-b-0 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:items-baseline md:gap-8 md:p-7 ${
                                    p.highlight ? "border-l-4 border-teal-600 bg-teal-50" : "border-l-4 border-transparent"
                                }`}
                            >
                                <h3 className="font-display text-xl font-extrabold text-ink">{p.name}</h3>
                                <p className="text-lg leading-relaxed text-ink-soft">{p.fit}</p>
                            </li>
                        ))}
                    </ul>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-4 text-base italic text-ink-soft">
                        Fall Safe Ready es un programa independiente y no está afiliado ni respaldado por estos
                        programas.
                    </p>
                </Reveal>
                <div className="mt-14 grid gap-8 sm:grid-cols-3">
                    {POINTS.map((p, i) => (
                        <Reveal key={p.title} delay={i * 0.1} className="h-full">
                            <div className="h-full">
                                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-ink text-brand-amber" aria-hidden="true">
                                    <p.icon className="h-6 w-6" />
                                </span>
                                <h3 className="mt-4 font-display text-xl font-extrabold text-ink">{p.title}</h3>
                                <p className="mt-2 text-lg leading-relaxed text-ink-soft">{p.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.15}>
                    <button
                        type="button"
                        data-testid="es-programs-host-workshop-button"
                        onClick={() => scrollToId("es-signup")}
                        className="btn-primary mt-12 text-lg"
                    >
                        Organice un taller
                        <ArrowRight className="h-5 w-5" aria-hidden="true" />
                    </button>
                </Reveal>
            </div>
        </section>
    );
}

function EsLearn() {
    const SKILLS = [
        { icon: Dumbbell, title: "Póngase más fuerte y estable", text: "Fuerza en las piernas y equilibrio que ayudan a prevenir las caídas desde el principio." },
        { icon: Zap, title: "Atájese", text: "Habilidades de reacción rápida y de dar pasos rápidos para cuando se tropiece o resbale." },
        { icon: ShieldCheck, title: "Caiga con más seguridad", text: "Hábitos de protección para la cabeza, las caderas y las muñecas, aprendidos gradualmente sobre colchonetas." },
        { icon: ArrowUpFromLine, title: "Levántese", text: "Cómo levantarse del piso con seguridad y qué hacer si no puede." },
    ];
    return (
        <section id="es-what-youll-learn" className="scroll-mt-24 pb-20 lg:pb-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal>
                    <SectionHead eyebrow="Las habilidades" title="Qué aprenderá" />
                </Reveal>
                <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {SKILLS.map((s, i) => (
                        <Reveal key={s.title} delay={i * 0.08} className="h-full">
                            <div
                                className="h-full rounded-3xl border border-clay bg-white p-8 transition-[border-color,transform] duration-200 hover:-translate-y-1 hover:border-teal-600/40"
                                data-testid={`es-skill-card-${i + 1}`}
                            >
                                <span className="grid h-14 w-14 place-items-center rounded-2xl bg-teal-50 text-teal-700" aria-hidden="true">
                                    <s.icon className="h-7 w-7" />
                                </span>
                                <h3 className="mt-6 font-display text-xl font-extrabold text-ink">{s.title}</h3>
                                <p className="mt-3 text-lg leading-relaxed text-ink-soft">{s.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function EsSteps() {
    const STEPS = [
        { n: "01", title: "Comience con una evaluación", text: "Un cuestionario de salud breve y tres pruebas simples usadas en programas de prevención de caídas (sentadillas en silla de 30 segundos, una prueba de equilibrio de pie y una caminata cronometrada)." },
        { n: "02", title: "Practique en un grupo pequeño", text: "Instrucción paciente y paso a paso en grupos pequeños con pocos participantes por instructor, y nadie es empujado más allá de lo que es adecuado para usted." },
        { n: "03", title: "Vea su progreso", text: "Repita las mismas pruebas al final y compare." },
        { n: "04", title: "Siga adelante", text: "Práctica continua para mantener las habilidades." },
    ];
    return (
        <section id="es-how-it-works" className="scroll-mt-24 pb-20 lg:pb-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal>
                    <SectionHead eyebrow="El programa" title="Cómo funciona" />
                </Reveal>
                <ol className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                    {STEPS.map((s, i) => (
                        <Reveal key={s.n} delay={i * 0.08} className="h-full">
                            <li className="h-full border-t-2 border-clay pt-6" data-testid={`es-step-${i + 1}`}>
                                <span className="font-display text-6xl font-black text-teal-600" aria-hidden="true">
                                    {s.n}
                                </span>
                                <h3 className="mt-3 font-display text-xl font-extrabold text-ink">{s.title}</h3>
                                <p className="mt-2 text-lg leading-relaxed text-ink-soft">{s.text}</p>
                            </li>
                        </Reveal>
                    ))}
                </ol>
                <div className="mt-16 grid items-center gap-10 lg:grid-cols-2">
                    <Reveal>
                        <div className="rounded-3xl border border-teal-600/25 bg-teal-50 p-8 lg:p-10">
                            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-teal-700 text-white" aria-hidden="true">
                                <CalendarCheckIcon />
                            </span>
                            <p className="mt-6 text-lg leading-relaxed text-ink" data-testid="es-steps-format-note">
                                Comience con un taller introductorio de una hora, que no incluye práctica de
                                caídas. Luego, si lo desea, continúe con el curso completo: ocho sesiones durante
                                cuatro semanas.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <img
                            src="https://images.pexels.com/photos/11674390/pexels-photo-11674390.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                            alt="Adultos de 60 y 70 años participando juntos en una clase de ejercicio grupal"
                            className="aspect-[4/3] w-full rounded-[2rem] border border-clay object-cover"
                            loading="lazy"
                            data-testid="es-steps-image"
                        />
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

const CalendarCheckIcon = () => (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 2v4M16 2v4" />
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M3 10h18M9 16l2 2 4-4" />
    </svg>
);

function EsResearch() {
    const POINTS = [
        "Un ensayo aleatorizado en Suecia con 200 adultos mayores de 65 años encontró que los participantes aprendieron técnicas de caída más seguras sin efectos adversos y conservaron las habilidades 20 meses después.",
        "Una revisión importante de 108 estudios (Cochrane, 2019) encontró que el ejercicio reduce la tasa de caídas en adultos mayores en aproximadamente un 23%, con la evidencia más sólida para el entrenamiento de equilibrio y funcional.",
        "Los programas que enseñan caídas seguras basadas en judo para adultos mayores están creciendo en Australia, Europa y Asia. Fall Safe Ready trae este enfoque al norte de Virginia.",
    ];
    return (
        <section className="border-y border-clay bg-sand py-20 lg:py-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal>
                    <SectionHead eyebrow="Evidencia" title="Basado en la investigación" />
                </Reveal>
                <div className="mt-12 grid gap-8 md:grid-cols-3">
                    {POINTS.map((p, i) => (
                        <Reveal key={i} delay={i * 0.1} className="h-full">
                            <div className="h-full border-t-2 border-teal-600 pt-6" data-testid={`es-research-point-${i + 1}`}>
                                <p className="text-lg leading-relaxed text-ink">{p}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function EsWho() {
    return (
        <section className="py-20 lg:py-28">
            <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
                <div>
                    <Reveal>
                        <SectionHead eyebrow="¿Es para usted?" title="Para quién es" />
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-6 text-lg leading-relaxed text-ink-soft">
                            Fall Safe Ready es para adultos de 55 años o más que viven de forma independiente y
                            quieren mantenerse activos, ya sea que haya tenido una caída, un susto o simplemente
                            quiera estar preparado. No necesita ser atlético. Si puede ponerse de pie desde una
                            silla, con o sin los brazos, puede comenzar. Muchas personas comienzan a los 60 años,
                            mientras están lo suficientemente fuertes para aprender rápido.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-8 font-display text-2xl font-extrabold leading-snug text-teal-700 sm:text-3xl">
                            Piense en ello como entrenamiento, como aprender a nadar.
                        </p>
                    </Reveal>
                </div>
                <Reveal delay={0.1}>
                    <img
                        src="https://images.unsplash.com/photo-1758798458635-f01402b40919?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHw0fHxzZW5pb3JzJTIwZ3JvdXAlMjBleGVyY2lzZSUyMGNsYXNzJTIwc3RyZXRjaGluZ3xlbnwwfHx8fDE3OTI2OTA1ODV8MA&ixlib=rb-4.1.0&q=85"
                        alt="Adultos de 60 y 70 años haciendo ejercicio juntos al aire libre en un parque"
                        className="aspect-[4/3] w-full rounded-[2rem] border border-clay object-cover"
                        loading="lazy"
                        data-testid="es-who-its-for-image"
                    />
                </Reveal>
            </div>
        </section>
    );
}

function EsAbout() {
    return (
        <section id="es-about" className="scroll-mt-24 pb-20 lg:pb-28">
            <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                <div>
                    <Reveal>
                        <SectionHead eyebrow="Acerca de" title="Conozca a Sarah" />
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-8 flex items-center gap-6">
                            <svg viewBox="0 0 120 120" className="h-44 w-44" role="img" aria-label="Monograma SG de la fundadora Sarah Gmyr">
                                <circle cx="60" cy="60" r="58" fill="#0B192C" />
                                <path d="M12 88c20 18 68 18 92-8" fill="none" stroke="#2DD4BF" strokeWidth="4" strokeLinecap="round" />
                                <circle cx="42" cy="34" r="7" fill="#F59E0B" />
                                <text x="60" y="72" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="900" fontSize="34" fill="#FDFBF7">
                                    SG
                                </text>
                            </svg>
                            <div>
                                <p className="font-display text-2xl font-extrabold text-ink">Sarah Gmyr</p>
                                <p className="text-lg text-ink-soft">Fundadora, Fall Safe Ready</p>
                            </div>
                        </div>
                    </Reveal>
                </div>
                <div>
                    <Reveal delay={0.1}>
                        <p className="text-lg leading-relaxed text-ink-soft">
                            Soy Sarah Gmyr y vivo en Arlington. Comencé mi carrera como maestra de escuela
                            primaria en EE. UU., el Reino Unido y Australia, y he pasado 18 años en comunicación
                            de salud y pública, incluido trabajo con el US Military Health System. Completé la
                            capacitación de líder del Programa Otago y estoy completando la capacitación de líder
                            SAIL (octubre de 2026) a través de la Northern Virginia Falls Prevention Alliance en
                            Marymount University.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-5 text-lg leading-relaxed text-ink-soft">
                            Fall Safe Ready une mi experiencia en enseñanza y salud. Lo estoy construyendo con un
                            instructor de judo y la asesoría de profesionales de la salud, porque todas las
                            personas merecen saber qué hacer cuando ocurre una caída, y seguir moviéndose con
                            confianza.
                        </p>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function EsFaq() {
    const FAQS = [
        ["¿Es seguro?", "La seguridad va primero. Cada participante completa un cuestionario de salud, los grupos son pequeños y cada habilidad se introduce lentamente sobre colchonetas. El taller introductorio no incluye práctica de caídas."],
        ["¿Necesito experiencia?", "Ninguna. Todo se enseña desde el principio, a su ritmo."],
        ["¿Es artes marciales?", "No. Algunas técnicas están adaptadas del judo, que ha enseñado a las personas a caer de manera segura por más de un siglo. No hay sparring, lanzamientos ni contacto."],
        ["¿Dónde y cuándo?", "Los primeros talleres serán en Arlington, Virginia, a principios de 2027. Únase a la lista de interés para enterarse de las fechas primero."],
        ["¿Cuánto cuesta?", "Los talleres introductorios serán gratuitos o de bajo costo. Los precios del curso se anunciarán con las primeras fechas."],
        ["¿Mi organización puede organizar un taller?", "Sí. Use el formulario de organización abajo y Sarah se comunicará con usted."],
    ];
    return (
        <section id="es-faq" className="scroll-mt-24 pb-20 lg:pb-28">
            <div className="mx-auto max-w-4xl px-5 sm:px-8">
                <Reveal>
                    <SectionHead eyebrow="Preguntas" title="Preguntas frecuentes" />
                </Reveal>
                <Reveal delay={0.1}>
                    <Accordion type="single" collapsible className="mt-10">
                        {FAQS.map(([q, a], i) => (
                            <AccordionItem key={i} value={`es-faq-${i}`} className="border-clay">
                                <AccordionTrigger
                                    data-testid={`es-faq-question-${i + 1}`}
                                    className="py-5 text-left text-lg font-bold text-ink hover:text-teal-700 hover:no-underline"
                                >
                                    {q}
                                </AccordionTrigger>
                                <AccordionContent className="text-lg leading-relaxed text-ink-soft">{a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Reveal>
            </div>
        </section>
    );
}

const EsField = ({ label, required = false, optional = false, error, htmlFor, children }) => (
    <div>
        <label htmlFor={htmlFor} className="field-label">
            {label}
            {required && <span aria-hidden="true"> *</span>}
            {optional && <span className="font-normal text-ink-soft/80"> (opcional)</span>}
        </label>
        {children}
        {error && (
            <p className="error-text" role="alert" data-testid={`${htmlFor}-error`}>
                {error}
            </p>
        )}
    </div>
);

function EsInterestForm() {
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm({ defaultValues: { interest_for: "Yo mismo" } });
    const [status, setStatus] = useState("idle");

    const onSubmit = async (data) => {
        try {
            await axios.post(`${API}/interest-signups`, { ...data, lang: "es" });
            reset();
            setStatus("success");
        } catch {
            setStatus("error");
        }
    };

    if (status === "success") {
        return (
            <div role="status" data-testid="es-interest-success" className="flex h-full flex-col items-start justify-center gap-4 rounded-3xl border border-teal-600/30 bg-teal-50 p-8">
                <CheckCircle2 className="h-10 w-10 text-teal-700" aria-hidden="true" />
                <p className="font-display text-2xl font-extrabold text-ink">
                    ¡Gracias! Será de los primeros en enterarse de las fechas de los talleres.
                </p>
                <button type="button" onClick={() => setStatus("idle")} className="inline-flex items-center rounded-full px-1 py-2 text-base font-bold text-teal-700 underline" data-testid="es-interest-reset">
                    Agregar otra persona
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="es-interest-form" className="h-full rounded-3xl border border-clay bg-white p-7 sm:p-9">
            <h3 className="font-display text-2xl font-extrabold text-ink">Sea el primero en saberlo</h3>
            <div className="mt-6 space-y-6">
                <EsField label="Nombre" required error={errors.first_name?.message} htmlFor="es-interest-first-name">
                    <input
                        id="es-interest-first-name"
                        data-testid="es-interest-first-name-input"
                        className="input-field"
                        autoComplete="given-name"
                        {...register("first_name", {
                            required: "Por favor escriba su nombre.",
                            maxLength: { value: 100, message: "Por favor use menos de 100 caracteres." },
                        })}
                    />
                </EsField>
                <EsField label="Correo electrónico" required error={errors.email?.message} htmlFor="es-interest-email">
                    <input
                        id="es-interest-email"
                        type="email"
                        data-testid="es-interest-email-input"
                        className="input-field"
                        autoComplete="email"
                        {...register("email", {
                            required: "Por favor escriba su correo electrónico.",
                            pattern: { value: EMAIL_RE, message: "Ese correo electrónico no parece válido." },
                        })}
                    />
                </EsField>
                <EsField label="Código postal" optional error={errors.zip_code?.message} htmlFor="es-interest-zip">
                    <input
                        id="es-interest-zip"
                        data-testid="es-interest-zip-input"
                        className="input-field"
                        inputMode="numeric"
                        autoComplete="postal-code"
                        {...register("zip_code", {
                            maxLength: { value: 10, message: "El código postal parece demasiado largo." },
                        })}
                    />
                </EsField>
                <fieldset>
                    <legend className="field-label">Me interesa para</legend>
                    <div className="space-y-3">
                        {["Yo mismo", "Un familiar", "Mi organización o clientes"].map((opt) => (
                            <label key={opt} className="flex items-center gap-3 py-2.5 text-lg text-ink">
                                <input
                                    type="radio"
                                    value={opt}
                                    data-testid={`es-interest-for-${opt.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="h-6 w-6 accent-teal-700"
                                    {...register("interest_for")}
                                />
                                {opt}
                            </label>
                        ))}
                    </div>
                </fieldset>
                <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                    data-testid="es-interest-honeypot"
                    {...register("company")}
                />
                {status === "error" && (
                    <p className="error-text" role="alert" data-testid="es-interest-submit-error">
                        Algo salió mal al enviar su solicitud. Inténtelo de nuevo.
                    </p>
                )}
                <button type="submit" disabled={isSubmitting} data-testid="es-interest-submit-button" className="btn-primary w-full text-lg">
                    {isSubmitting ? "Enviando…" : "Manténgame informado"}
                </button>
            </div>
        </form>
    );
}

function EsPartnerForm() {
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm();
    const [status, setStatus] = useState("idle");

    const onSubmit = async (data) => {
        try {
            await axios.post(`${API}/partner-inquiries`, { ...data, lang: "es" });
            reset();
            setStatus("success");
        } catch {
            setStatus("error");
        }
    };

    if (status === "success") {
        return (
            <div role="status" data-testid="es-partner-success" className="flex h-full flex-col items-start justify-center gap-4 rounded-3xl border border-teal-600/30 bg-teal-50 p-8">
                <CheckCircle2 className="h-10 w-10 text-teal-700" aria-hidden="true" />
                <p className="font-display text-2xl font-extrabold text-ink">
                    ¡Gracias! Sarah responderá dentro de dos días hábiles.
                </p>
                <button type="button" onClick={() => setStatus("idle")} className="inline-flex items-center rounded-full px-1 py-2 text-base font-bold text-teal-700 underline" data-testid="es-partner-reset">
                    Enviar otra consulta
                </button>
            </div>
        );
    }

    const ORG_TYPES = [
        "Comunidad de adultos mayores",
        "Clínica de fisioterapia o terapia ocupacional",
        "Centro comunitario o de recreación",
        "Comunidad religiosa",
        "Coalición de prevención de caídas",
        "Otro",
    ];

    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="es-partner-form" className="h-full rounded-3xl border border-clay bg-white p-7 sm:p-9">
            <h3 className="font-display text-2xl font-extrabold text-ink">Traiga Fall Safe Ready a su organización</h3>
            <div className="mt-6 space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                    <EsField label="Nombre" required error={errors.name?.message} htmlFor="es-partner-name">
                        <input
                            id="es-partner-name"
                            data-testid="es-partner-name-input"
                            className="input-field"
                            autoComplete="name"
                            {...register("name", { required: "Por favor escriba su nombre." })}
                        />
                    </EsField>
                    <EsField label="Organización" required error={errors.organization?.message} htmlFor="es-partner-organization">
                        <input
                            id="es-partner-organization"
                            data-testid="es-partner-organization-input"
                            className="input-field"
                            autoComplete="organization"
                            {...register("organization", { required: "Por favor escriba su organización." })}
                        />
                    </EsField>
                </div>
                <div className="grid gap-6 sm:grid-cols-2">
                    <EsField label="Cargo" optional error={errors.role?.message} htmlFor="es-partner-role">
                        <input id="es-partner-role" data-testid="es-partner-role-input" className="input-field" {...register("role")} />
                    </EsField>
                    <EsField label="Correo electrónico" required error={errors.email?.message} htmlFor="es-partner-email">
                        <input
                            id="es-partner-email"
                            type="email"
                            data-testid="es-partner-email-input"
                            className="input-field"
                            autoComplete="email"
                            {...register("email", {
                                required: "Por favor escriba su correo electrónico.",
                                pattern: { value: EMAIL_RE, message: "Ese correo electrónico no parece válido." },
                            })}
                        />
                    </EsField>
                </div>
                <div className="grid gap-6 sm:grid-cols-2">
                    <EsField label="Teléfono" optional error={errors.phone?.message} htmlFor="es-partner-phone">
                        <input id="es-partner-phone" type="tel" data-testid="es-partner-phone-input" className="input-field" autoComplete="tel" {...register("phone")} />
                    </EsField>
                    <EsField label="Tipo de organización" optional error={errors.org_type?.message} htmlFor="es-partner-org-type">
                        <select id="es-partner-org-type" data-testid="es-partner-org-type-select" className="input-field" defaultValue="" {...register("org_type")}>
                            <option value="">Elija una opción</option>
                            {ORG_TYPES.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </select>
                    </EsField>
                </div>
                <EsField label="Mensaje" optional error={errors.message?.message} htmlFor="es-partner-message">
                    <textarea
                        id="es-partner-message"
                        data-testid="es-partner-message-input"
                        className="textarea-field"
                        {...register("message", { maxLength: { value: 4000, message: "Por favor use menos de 4000 caracteres." } })}
                    />
                </EsField>
                <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                    data-testid="es-partner-honeypot"
                    {...register("website")}
                />
                {status === "error" && (
                    <p className="error-text" role="alert" data-testid="es-partner-submit-error">
                        Algo salió mal al enviar su consulta. Inténtelo de nuevo.
                    </p>
                )}
                <button type="submit" disabled={isSubmitting} data-testid="es-partner-submit-button" className="btn-primary w-full text-lg">
                    {isSubmitting ? "Enviando…" : "Enviar consulta"}
                </button>
            </div>
        </form>
    );
}

function EsSignup() {
    return (
        <section id="es-signup" className="scroll-mt-24 bg-ink py-20 lg:py-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal>
                    <SectionHead eyebrow="Comience hoy" title="Únase a la lista de interés" dark />
                </Reveal>
                <div className="mt-12 grid gap-8 lg:grid-cols-2">
                    <Reveal delay={0.05} className="h-full">
                        <EsInterestForm />
                    </Reveal>
                    <Reveal delay={0.15} className="h-full">
                        <EsPartnerForm />
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function EsFooter() {
    return (
        <footer className="bg-ink pb-10 pt-16 text-cream/80">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
                    <div>
                        <div className="flex items-center gap-2.5">
                            <LogoMark />
                            <span className="font-display text-xl font-extrabold tracking-tight text-cream">Fall Safe Ready</span>
                        </div>
                        <p className="mt-5 text-lg text-cream">Arlington, Virginia</p>
                        <a
                            href="mailto:SarahGmyr@gmail.com"
                            data-testid="es-footer-email"
                            className="mt-1 inline-block text-lg font-semibold text-cream underline decoration-brand-amber decoration-2 underline-offset-4 transition-colors duration-200 hover:text-brand-amber"
                        >
                            SarahGmyr@gmail.com
                        </a>
                        <div className="mt-5">
                            <a
                                href="/"
                                data-testid="es-footer-lang-en"
                                className="inline-flex h-12 items-center rounded-full border-2 border-cream px-5 text-base font-semibold text-cream transition-colors duration-200 hover:bg-cream hover:text-ink"
                            >
                                View in English
                            </a>
                        </div>
                    </div>
                    <div className="max-w-xl text-lg leading-relaxed text-cream/90">
                        <p>
                            Las clases de Fall Safe Ready son educativas y no son tratamiento médico. Consulte a
                            su médico antes de comenzar un nuevo programa de ejercicios.
                        </p>
                        <p className="mt-3">Estadísticas: CDC y National Council on Aging.</p>
                    </div>
                </div>
                <div className="mt-12 border-t border-cream/15 pt-6">
                    <p className="text-base text-cream/80">© 2026 Fall Safe Ready</p>
                </div>
            </div>
        </footer>
    );
}

export default function EsLanding() {
    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
        let lenis;
        let raf = 0;
        let cancelled = false;
        import("lenis").then(({ default: Lenis }) => {
            if (cancelled) return;
            lenis = new Lenis({ duration: 1.1, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
            window.__lenis = lenis;
            const loop = (time) => {
                lenis.raf(time);
                raf = requestAnimationFrame(loop);
            };
            raf = requestAnimationFrame(loop);
        });
        return () => {
            cancelled = true;
            cancelAnimationFrame(raf);
            if (lenis) lenis.destroy();
            window.__lenis = undefined;
        };
    }, []);

    return (
        <div className="min-h-screen bg-cream font-body text-ink">
            <a
                href="#es-main"
                className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-full focus:bg-ink focus:px-5 focus:py-3 focus:text-cream"
            >
                Ir al contenido
            </a>
            <div className="grain-overlay" aria-hidden="true" />
            <EsHeader />
            <main id="es-main">
                <EsHero />
                <Marquee items={["Atájese", "Caiga con más seguridad", "Levántese", "Más fuerte y estable"]} />
                <EsStats />
                <EsPrevention />
                <EsPrograms />
                <EsLearn />
                <EsSteps />
                <EsResearch />
                <EsWho />
                <EsAbout />
                <EsFaq />
                <EsSignup />
            </main>
            <EsFooter />
        </div>
    );
}